import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Clock, AlertCircle, XCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { formatFCFA } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll up to ~3 minutes: fast (1.5s) for the first 20 attempts, slower (3s) after.
const FAST_INTERVAL_MS = 1500;
const SLOW_INTERVAL_MS = 3000;
const FAST_ATTEMPTS = 20;
const MAX_ATTEMPTS = 80;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const orderId = params.get('order_id') || '';
  const { clearCart } = useCart();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);
  const cartCleared = useRef(false);

  useEffect(() => {
    if (!orderId) {
      setError("Identifiant de commande manquant.");
      return;
    }
    let cancelled = false;

    const pollOnce = async (n) => {
      try {
        const res = await axios.get(`${API}/orders/${orderId}/public`);
        if (cancelled) return;
        setStatus(res.data);
        const ps = (res.data.payment_status || '').toLowerCase();
        if (ps === 'paid') {
          if (!cartCleared.current) { cartCleared.current = true; clearCart(); }
          return; // stop polling
        }
        if (ps === 'cancelled' || ps === 'failed' || ps === 'expired') {
          return; // terminal state
        }
      } catch {
        // ignore and retry
      }
      if (n < MAX_ATTEMPTS && !cancelled) {
        const next = n < FAST_ATTEMPTS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
        setTimeout(() => setTick(t => t + 1), next);
      }
    };

    pollOnce(tick);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, tick]);

  const ps = (status?.payment_status || '').toLowerCase();
  const paid = ps === 'paid';
  const failedLike = ps === 'cancelled' || ps === 'failed' || ps === 'expired';
  const pending = status && !paid && !failedLike;

  return (
    <div data-testid="payment-success-page" className="pt-28 pb-24 min-h-screen flex items-center justify-center">
      <div className="text-center max-w-md px-4">
        {error ? (
          <>
            <AlertCircle size={48} className="mx-auto mb-6" style={{ color: '#B80000' }} />
            <h1 className="font-serif text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Une Erreur est Survenue
            </h1>
            <p className="text-sm text-black/60 mb-8">{error}</p>
          </>
        ) : paid ? (
          <>
            <CheckCircle2 size={56} className="mx-auto mb-6" style={{ color: '#B80000' }} />
            <h1 data-testid="payment-confirmed-title" className="font-serif text-5xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Commande Confirmée
            </h1>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-2">
              Commande #{status.order_id}
            </p>
            <p className="text-2xl font-light mb-6">{formatFCFA(status.total)}</p>
            <p className="text-sm text-black/70 mb-2 font-medium">
              Le livreur vous contactera sous peu.
            </p>
            <p className="text-xs text-black/50 mb-10">
              Un e-mail de confirmation a été envoyé à {status.customer_email}.
            </p>
          </>
        ) : failedLike ? (
          <>
            <XCircle size={48} className="mx-auto mb-6" style={{ color: '#B80000' }} />
            <h1 className="font-serif text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Paiement Non Abouti
            </h1>
            <p className="text-sm text-black/60 mb-2">
              {ps === 'cancelled' && "Le paiement a été annulé."}
              {ps === 'failed' && "Le paiement a échoué."}
              {ps === 'expired' && "La session de paiement a expiré."}
            </p>
            <p className="text-sm text-black/60 mb-10">Vous pouvez réessayer depuis votre panier.</p>
          </>
        ) : pending ? (
          <>
            <Clock size={48} className="mx-auto mb-6 animate-pulse" style={{ color: '#B80000' }} />
            <h1 className="font-serif text-4xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Vérification en cours
            </h1>
            <p className="text-sm text-black/60 mb-3">
              Votre paiement est en cours de validation par SenePay.
            </p>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-black/40 mb-8">
              Cela prend quelques secondes · Ne fermez pas la page
            </p>
          </>
        ) : (
          <>
            <Clock size={48} className="mx-auto mb-6 animate-pulse" style={{ color: '#B80000' }} />
            <p className="text-sm text-black/60">Chargement...</p>
          </>
        )}
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
          <Link to="/shop" className="btn-primary inline-block">Continuer les Achats</Link>
          <Link to="/account" className="btn-secondary inline-block">Mes Commandes</Link>
        </div>
      </div>
    </div>
  );
}
